/**
 * Narrates how a recorded round's fan total became the points in
 * `round.payments` — full transparency from fan count to final settlement,
 * including why a player pays zero (wasn't the discarder, wasn't ready,
 * Fun mode doesn't charge losers at all) rather than just showing who moved.
 *
 * Built entirely from data already stored on the round record plus the
 * game's own settings snapshot — this never recomputes a payment, only
 * explains one that's already been calculated and locked in, so it can
 * never disagree with the real settlement (src/rules/hkos/payout.ts stays
 * the single source of truth for the numbers).
 */

import type { Game, RoundRecord } from '../game/types';
import { activePayoutMethod, type HkosSettings } from '../rules/hkos';

// Deliberately not imported from ./helpers — helpers.ts's formatRoundListItem
// calls into this module, so importing back from helpers.ts would make the
// two files circularly dependent. esc/playerName are tiny enough that a
// second copy here is cheaper than untangling that.
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function playerName(game: Game, id: string | undefined): string {
  if (!id) return '?';
  return game.players.find((p) => p.id === id)?.name ?? id;
}

function deltaOf(round: RoundRecord, playerId: string): number {
  return round.payments.find((p) => p.player === playerId)?.delta ?? 0;
}

/**
 * Comma-grouped, matching helpers.ts's formatDelta (not imported here — see
 * the note above on why). With an exponential payout base and a high fan
 * cap, a single hand's points can run into the thousands or tens of
 * thousands (e.g. 2^15 = 32,768), and an ungrouped run of digits is hard to
 * read correctly at a glance.
 */
function fmt(n: number): string {
  return Math.abs(n).toLocaleString('en-US');
}

function explainSeriousWin(game: Game, round: RoundRecord, settings: HkosSettings): string {
  const s = settings.serious;
  const fan = round.effectiveFan ?? 0;
  const base = round.basePoints ?? 0;
  const winnerName = esc(playerName(game, round.winner));
  const dealerIsWinner = round.dealer === round.winner;
  const dealerIsPayer = round.payments.some((p) => p.player === round.dealer && p.delta < 0);

  const lines: string[] = [];

  const curve =
    activePayoutMethod(settings) === 'multiplier'
      ? `${fan} × ${s.multiplier ?? 1} (multiplier)`
      : `${s.payoutBase}<sup>${fan}</sup> (payout base)`;
  lines.push(
    `${fan} fan${round.isLimit ? ' (limit hand — capped here regardless of the real pattern total)' : ''} ` +
      `→ base = ${curve} × ${s.baseUnit} unit${s.baseUnit === 1 ? '' : 's'} = <strong>${fmt(base)}</strong> point${base === 1 ? '' : 's'}.`,
  );

  lines.push(
    round.selfDraw
      ? `Self-draw — all three other players pay, not just one.`
      : s.discardPayment === 'discarderPaysAll'
        ? `${esc(playerName(game, round.discarder))} discarded the winning tile — under "discarder pays all", only they pay; the other two owe nothing.`
        : `${esc(playerName(game, round.discarder))} discarded the winning tile and pays double under "discarder pays double"; the other two pay the standard share.`,
  );

  if (s.dealerDoublePayout) {
    lines.push(
      dealerIsWinner
        ? `${winnerName} is the dealer, so every payment to them doubles.`
        : dealerIsPayer
          ? `${esc(playerName(game, round.dealer))} is the dealer, so their payment doubles — the others don't.`
          : `The dealer isn't the winner or a payer this round, so the dealer-double rule doesn't apply.`,
    );
  }

  const perPlayer = game.players
    .map((p) => {
      if (p.id === round.winner) {
        return `${esc(p.name)} <strong>receives ${fmt(deltaOf(round, p.id))}</strong>`;
      }
      const delta = deltaOf(round, p.id);
      if (delta === 0) {
        const why = round.selfDraw
          ? ''
          : s.discardPayment === 'discarderPaysAll'
            ? "wasn't the discarder"
            : '';
        return `${esc(p.name)} pays 0${why ? ` (${why})` : ''}`;
      }
      return `${esc(p.name)} pays ${fmt(delta)}`;
    })
    .join(', ');

  lines.push(perPlayer + '.');
  return lines.map((l) => `<p>${l}</p>`).join('');
}

function explainFunWin(game: Game, round: RoundRecord, settings: HkosSettings): string {
  const fan = round.effectiveFan ?? 0;
  const base = round.basePoints ?? 0;
  const winnerName = esc(playerName(game, round.winner));

  const lines = [
    `${fan} fan${round.isLimit ? ' (limit hand)' : ''} × ${settings.fun.multiplier} (Fun mode multiplier) = <strong>${fmt(base)}</strong> point${base === 1 ? '' : 's'}.`,
    `${winnerName} <strong>receives ${fmt(base)}</strong>, while the other players pay 0 (Fun Mode never charges anyone else).`,
  ];
  return lines.map((l) => `<p>${l}</p>`).join('');
}

function explainSeriousDraw(game: Game, round: RoundRecord, settings: HkosSettings): string {
  const s = settings.serious;
  const lines: string[] = [];

  if (!s.tenpaiPaymentOnDraw) {
    lines.push(`Tenpai payment on draw is off for this table — a draw never moves anyone's points.`);
    return lines.map((l) => `<p>${l}</p>`).join('');
  }

  const ready = round.tenpai ?? [];
  const readyCount = game.players.filter((p) => ready.includes(p.id)).length;
  const notReadyCount = game.players.length - readyCount;

  if (readyCount === 0 || notReadyCount === 0) {
    lines.push(
      `${readyCount === 0 ? 'Nobody' : 'Everybody'} was tenpai (ready) — a draw only pays when the room is genuinely split, so no points change hands.`,
    );
    return lines.map((l) => `<p>${l}</p>`).join('');
  }

  const unit = s.tenpaiPaymentUnits * s.baseUnit;
  lines.push(
    `${readyCount} player${readyCount === 1 ? ' was' : 's were'} tenpai (ready), ${notReadyCount} ${notReadyCount === 1 ? 'was' : 'were'} not — each not-ready player pays ${fmt(unit)} point${unit === 1 ? '' : 's'} to each ready player.`,
  );

  const perPlayer = game.players
    .map((p) => {
      const delta = deltaOf(round, p.id);
      const isReady = ready.includes(p.id);
      return `${esc(p.name)} (${isReady ? 'ready' : 'not ready'}) ${delta >= 0 ? 'receives' : 'pays'} ${fmt(delta)}`;
    })
    .join(', ');
  lines.push(perPlayer + '.');
  return lines.map((l) => `<p>${l}</p>`).join('');
}

function explainFunDraw(): string {
  return `<p>Fun Mode never pays out on a draw — nobody's total changes, regardless of who was tenpai (ready).</p>`;
}

function settlementBodyHtml(game: Game, round: RoundRecord): string {
  const settings = game.settings as HkosSettings;

  return round.outcome === 'win'
    ? settings.mode === 'fun'
      ? explainFunWin(game, round, settings)
      : explainSeriousWin(game, round, settings)
    : settings.mode === 'fun'
      ? explainFunDraw()
      : explainSeriousDraw(game, round, settings);
}

/**
 * The same explanation as `formatSettlementExplanation`, as plain text — one
 * entry per paragraph, no markup — for the PDF report, which has no HTML to
 * render it into. Derived from the exact same builders (not a second copy of
 * the wording) so the PDF can never disagree with what the screen says.
 */
export function settlementExplanationLines(game: Game, round: RoundRecord): string[] {
  return settlementBodyHtml(game, round)
    .split('</p>')
    .map((chunk) =>
      chunk
        .replace(/<sup>(.*?)<\/sup>/g, '^$1')
        .replace(/<[^>]+>/g, '')
        .replace(/&quot;/g, '"')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .trim(),
    )
    .filter((line) => line.length > 0);
}

/**
 * The full "how the points work out" block for one round, collapsed by
 * default (a `<details>` disclosure) so it stays available without
 * cluttering the round list on screen. `initPrintExpand` below forces every
 * one of these open for Download-as-PDF / Ctrl+P — there's no way to click a
 * `<summary>` on paper, and a plain CSS override on the collapsed content
 * doesn't reliably beat the browser's own `<details>` hiding.
 */
export function formatSettlementExplanation(game: Game, round: RoundRecord): string {
  const body = settlementBodyHtml(game, round);

  return `
    <details class="settlement-explain">
      <summary>How the points work out</summary>
      <div class="settlement-lines">${body}</div>
    </details>
  `;
}

/**
 * Forces every `.settlement-explain` open right now, one-way (no restore).
 * Used by the "Download as PDF" button's own click handler as the reliable
 * path: `beforeprint`/`afterprint` (see `initPrintExpand` below) don't fire
 * consistently on every mobile browser, so a button that only relied on
 * those could silently print the collapsed state. Calling this directly,
 * synchronously, right before `window.print()` doesn't depend on the
 * browser supporting those events at all. Left open afterward rather than
 * restored — this only ever runs on the terminal Final Results screen, so a
 * round staying expanded post-print is a harmless, easily-undone side
 * effect, and avoids racing an OS print pipeline that may rasterize the
 * page asynchronously after this function returns.
 */
export function expandAllSettlementExplanations(): void {
  for (const d of document.querySelectorAll<HTMLDetailsElement>('.settlement-explain:not([open])')) {
    d.open = true;
  }
}

/**
 * Best-effort fallback for a print triggered outside the app's own button
 * (native Ctrl+P, browser print menu) — `beforeprint`/`afterprint` are
 * reliable on desktop, so this restores the collapsed state afterward there.
 * Not the guaranteed path; see `expandAllSettlementExplanations` above for
 * that. Call once at startup.
 */
export function initPrintExpand(): void {
  let reopen: HTMLDetailsElement[] = [];
  window.addEventListener('beforeprint', () => {
    reopen = [...document.querySelectorAll<HTMLDetailsElement>('.settlement-explain:not([open])')];
    for (const d of reopen) d.open = true;
  });
  window.addEventListener('afterprint', () => {
    for (const d of reopen) d.open = false;
    reopen = [];
  });
}
